import React, { useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { truncateString } from "utils/UtilityFuncs";
import API from "services/axios";
import authHeader from "services/auth-header";
import { setNotification } from "features/Notification/notificationSlice";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "components/Spinner";
import EmailBulkActions from "pages/Sales/Inbox/EmailComponents/EmailBulkActions";
import useInbox from "../InboxTab/InboxContext";

export default function EmailHistory({
  hideModal,
  emails,
  setEmails,
  handleFilterClick,
}) {
  /* ******************** All States ************************* */
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.userInfo);

  const {
    messageId,
    setMessageId,
    selectedLabel,
    getFilteredLabelNames,
    getSenderName,
    formatGmailDate,
    fromEmail,
    labels,
    defaultLabels,
    handleBulkReadStatus,
    handleBulkDelete,
    setRefreshEmails,
    selectedGrantEmail,
  } = useInbox();

  const [loading, setLoading] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState([]);

  /* Pagination ********* */
  const [limit, setLimit] = useState(20);
  const [pageTokensList, setPageTokensList] = useState([]);
  const [previousToken, setPreviousToken] = useState(null);
  const [nextToken, setNextToken] = useState(null);

  /* ******************** All Functions ************************* */

  const getLabelBadgeClass = (labelName) => {
    const value = (labelName || "").toLowerCase();

    if (value === "inbox") return "bg-secondary";
    if (value === "sent") return "bg-sent";
    if (value === "archived_mails") return "bg-primary";
    if (value === "archived") return "bg-primary";
    if (value === "trash") return "bg-danger";
    if (value === "spam") return "bg-danger";

    return "bg-info";
  };

  const emailRowBgColor = (email) => {
    const labels = getFilteredLabelNames(email?.folders);

    if (labels.includes("Sent")) {
      return `sent-row ${email?.unread ? "unread" : "read"}`;
    }

    if (selectedEmails.includes(email?.id)) {
      return "bg-primary bg-opacity-25";
    }

    if (selectedGrantEmail === email.from?.[0]?.email) {
      return "bg-success bg-opacity-10";
    }

    if (email?.unread) {
      return "bg-white";
    }

    return "bg-light bg-opacity-50";
  };

  const handleAllCheck = (e) => {
    if (e.target.checked) {
      setSelectedEmails(emails.map((email) => email?.id));
    } else {
      setSelectedEmails([]);
    }
  };

  const handleEmailCheck = (emailId) => {
    let updatedSelectedEmails = [...selectedEmails];
    if (updatedSelectedEmails.includes(emailId)) {
      updatedSelectedEmails = updatedSelectedEmails.filter(
        (id) => id !== emailId,
      );
    } else {
      updatedSelectedEmails.push(emailId);
    }
    setSelectedEmails(updatedSelectedEmails);
  };

  const handleEmailReadStatus = async (messageId, isRead) => {
    try {
      if (!messageId) {
        return dispatch(
          setNotification({
            message: "Invalid email selected",
            type: "error",
          }),
        );
      }

      setLoading(true);
      let success = await handleBulkReadStatus([messageId], isRead);
      if (success) {
        setSelectedEmails([]);
        // refreshLabels();;
        await getFromHistory();
        setRefreshEmails(true);

        dispatch(
          setNotification({
            message: `Email marked as ${isRead ? "read" : "unread"}`,
            type: "success",
          }),
        );
      } else {
        dispatch(
          setNotification({
            message: "Failed to update email status",
            type: "error",
          }),
        );
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      if (!messageId) {
        return dispatch(
          setNotification({
            message: "Invalid email selected",
            type: "error",
          }),
        );
      }

      setLoading(true);
      let success = await handleBulkDelete([messageId]);
      if (success) {
        setSelectedEmails([]);
        // refreshLabels();;
        await getFromHistory();
        setRefreshEmails(true);
      } else {
        dispatch(
          setNotification({
            message: "Failed to delete email",
            type: "error",
          }),
        );
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  const handlePagination = (receivedPageToken, currentPageToken) => {
    // Update next token
    setNextToken(receivedPageToken);
    setPreviousToken(currentPageToken);

    // add received token to page tokens list if not already present
    let updatedTokensList = [...pageTokensList];
    if (receivedPageToken && !updatedTokensList.includes(receivedPageToken)) {
      updatedTokensList.push(receivedPageToken);
    }

    setPageTokensList(updatedTokensList);
  };

  // get Emails history of fromEmail

  const getFromHistory = async (pageToken = null) => {
    try {
      setLoading(true);
      if (!fromEmail) {
        setLoading(false);
        return;
      }
      const requestPayload = {
        limit,
        pageToken: pageToken || null,
      };

      if (fromEmail) {
        requestPayload.fromEmail = `from:${fromEmail} OR to:${fromEmail} -in:TRASH`;
        requestPayload.toEmail = `from:${fromEmail} OR to:${fromEmail} -in:TRASH`;
      }

      const { data } = await API.post("/emails/by-from-email", requestPayload, {
        headers: authHeader(),
      });
      if (data?.success) {
        // if no emails returned, clear next token
        if (data?.emails?.data?.length === 0) {
          setNextToken(null);
          // hideModal();
        }
        setEmails(data?.emails?.data || []);
        let receivedPageToken = data?.emails?.nextCursor || null;
        handlePagination(receivedPageToken, pageToken);
        setLoading(false);
        return data?.emails?.data;
      } else {
        setLoading(false);
        dispatch(
          setNotification({
            message: "Failed to fetch emails",
            type: "error",
          }),
        );
      }
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err?.response?.data.message || err.message,
          type: "error",
        }),
      );
    }
  };

  // Remove archived folder from all emails in current view

  const removeFromArchivedFolder = async () => {
    try {
      setLoading(true);
      const messageIdsArray = emails.map((email) => email?.id);
      const { data } = await API.post(
        "/emails/remove-folder-from-emails",
        {
          messageIdsArray,
        },
        { headers: authHeader() },
      );
      if (data?.success) {
        // Handle success scenario
      } else {
        throw new Error(data?.message || "Failed to add project");
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  /* ******************** All UseEffects ************************* */
  const getOrderedFilterLabels = () => {
    const allLabels = [...defaultLabels, ...labels];
    const seen = new Set();

    const norm = (name) => (name || "").toLowerCase();

    const pick = (predicate) => {
      const picked = [];
      allLabels.forEach((label) => {
        const key = norm(label.name);
        if (seen.has(key)) return;
        if (predicate(label, key)) {
          seen.add(key);
          picked.push(label);
        }
      });
      return picked;
    };

    const inbox = pick((_, key) => key === "inbox");
    const sent = pick((_, key) => key === "sent");
    const trash = pick((_, key) => key === "trash");
    // const archived = pick(
    //   (_, key) => key === "archived" || key === "archived_mails",
    // );
    const others = pick(() => true);

    // return [...inbox, ...sent, ...trash, ...archived, ...others];
    return [...inbox, ...sent, ...trash, ...others];
  };

  useEffect(() => {
    let timeout = setTimeout(() => {
      if (fromEmail) {
        setPageTokensList([]);
        setPreviousToken(null);
        setNextToken(null);
        getFromHistory();
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [fromEmail, limit]);

  return (
    <div
      className="position-relatdive"
      style={{
        zIndex: 10000,
      }}
    >
      {loading && <Spinner />}
      {/*   Select All And Bulk Actions ************************* */}
      <div className="d-flex justify-content-between align-items-center mb-2 px-2">
        <div className="w-75">
          <div className="d-flex align-items-center gap-2">
            <input
              type="checkbox"
              style={{
                marginBottom: 2,
              }}
              checked={
                emails.length > 0 && selectedEmails.length === emails.length
              }
              onChange={handleAllCheck}
              className="form-check-input"
            />
            {selectedEmails.length > 0 && (
              <>
                <div
                  className="d-flex align-items-center mx-1"
                  style={{ gap: 18 }}
                >
                  <EmailBulkActions
                    refreshFunc={async () => {
                      await getFromHistory();
                      setRefreshEmails(true);
                      if (emails.length === 0) {
                        hideModal(true);
                      }
                      setMessageId(null);
                      setSelectedEmails([]);
                    }}
                    selectedEmails={selectedEmails}
                  />
                </div>
              </>
            )}
            {/* <button
              onClick={removeFromArchivedFolder}
              className="btn btn-sm btn-danger"
            >
              Remove Archived Folder
            </button> */}
            {/* Label filter badges */}
            <div
              className="d-flex align-items-center flex-wrap ms-0"
              style={{ gap: 6 }}
            >
              {getOrderedFilterLabels().map((label) => (
                <button
                  key={label.labelId || label.id || label.name}
                  type="button"
                  className={`badge px-2 py-1 rounded-pill border-0 ${getLabelBadgeClass(
                    label.name,
                  )} text-white`}
                  onClick={() => {
                    handleFilterClick(label.name);
                  }}
                >
                  {label.name === "archived_mails" ? "Archived" : label.name}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div>
          <div className="d-flex align-items-center">
            <div className="flex-center">
              <button
                onClick={(e) => {
                  getFromHistory();
                }}
                title="Refresh"
                disabled={loading}
                className="btn btn-sm btn-outline-success px-1 py-0 me-2"
              >
                <i
                  style={{
                    fontSize: "18px",
                    width: "18px",
                  }}
                  className="flex-center mdi mdi-refresh "
                ></i>
              </button>
            </div>
            {/* Limit Select Drop Down */}
            <Dropdown>
              <Dropdown.Toggle
                as={"div"}
                style={{ cursor: "pointer" }}
                className="no-icon d-inline-block"
                variant=""
              >
                <span className="me-2">Limit: {limit}</span>
                <i className="mdi mdi-chevron-down fs-4"></i>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item
                  onClick={() => {
                    setLimit(10);
                  }}
                >
                  10
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => {
                    setLimit(25);
                  }}
                >
                  25
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => {
                    setLimit(50);
                  }}
                >
                  50
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            {/* Pagination buttons */}
            <button
              onClick={() => {
                let _previousToken = null;
                if (pageTokensList?.length > 1 && previousToken) {
                  const currentIndex = pageTokensList.indexOf(previousToken);
                  if (currentIndex > 0) {
                    _previousToken = pageTokensList[currentIndex - 1];
                  }
                }

                if (!previousToken) {
                  setPageTokensList([]);
                }

                getFromHistory(_previousToken);
              }}
              disabled={!previousToken}
              className="btn btn-sm px-1 py-0 border-0"
              title="previous page"
            >
              <i className="mdi mdi-chevron-left fs-3 "></i>
            </button>
            <button
              onClick={() => {
                getFromHistory(nextToken);
              }}
              disabled={!nextToken}
              className="btn btn-sm px-1 py-0 border-0"
              title="next page"
            >
              <i className="mdi mdi-chevron-right fs-3 "></i>
            </button>
          </div>
        </div>
      </div>

      <hr className="mt-0 mb-0" />

      <table className="table table-sm ">
        <tbody>
          {/* Emails List */}
          {emails?.map((email) => (
            <tr
              className={`email-row border-start border-end ${emailRowBgColor(
                email,
              )}`}
              key={email.id}
              style={{
                cursor: "pointer",
                overflow: "hidden",
              }}
              onClick={() => {
                setMessageId(email.id);
              }}
            >
              <td
                style={{ width: "30px" }}
                onClick={(e) => e.stopPropagation()}
                className=""
              >
                <input
                  type="checkbox"
                  checked={selectedEmails.includes(email?.id)}
                  onChange={() => handleEmailCheck(email?.id)}
                  className="form-check-input"
                />
              </td>
              <td className="text-truncate" style={{ width: "220px" }}>
                <span
                  className={`text-mute-2 d-flex align-items-center ${
                    email?.unread ? "fw-bold" : ""
                  }`}
                >
                  {getSenderName(email)}
                </span>
              </td>
              <td>
                <div className="text-truncate " style={{ width: "550px" }}>
                  <span className="ms-3">
                    {getFilteredLabelNames(
                      email?.folders,
                      selectedLabel,
                      true,
                    ).map((labelName) => (
                      <span
                        key={labelName}
                        className={`badge ${getLabelBadgeClass(
                          labelName,
                        )} fs-6 text-white me-1`}
                      >
                        {labelName === "archived_mails"
                          ? "Archived"
                          : labelName}
                      </span>
                    ))}
                  </span>
                  <span
                    className={`text-mute-2 ${email?.unread ? "fw-bold" : ""}`}
                  >
                    {email?.subject}
                  </span>{" "}
                  - {truncateString(email?.snippet, 100)}
                </div>
              </td>
              {/* <td>
                <span className="ms-3">
                  {getFilteredLabelNames(email?.folders).map((labelName) => (
                    <span
                      key={labelName}
                      className="badge bg-info fs-6 text-white me-1"
                    >
                      {labelName === "archived_mails" ? "Archived" : labelName}
                    </span>
                  ))}
                </span>
              </td> */}
              <td
                className={`d-none d-md-table-cell small ${
                  email?.unread ? "fw-bold text-mute-2" : "text-muted"
                }`}
              >
                {formatGmailDate(email?.date)}
              </td>
              <td
                className="email-row__action-cell"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="email-row__action-panel">
                  {selectedLabel !== "TRASH" && (
                    <button
                      type="button"
                      className="email-row__action"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDeleteMessage(email?.id);
                      }}
                      disabled={loading}
                      title="Delete email"
                      aria-label="Delete email"
                    >
                      <i className="mdi mdi-trash-can-outline"></i>
                    </button>
                  )}
                  <button
                    type="button"
                    className="email-row__action"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleEmailReadStatus(email?.id, false);
                    }}
                    disabled={loading}
                    title="Mark as unread"
                    aria-label="Mark as unread"
                  >
                    <i className="mdi mdi-email-outline"></i>
                  </button>
                  <button
                    type="button"
                    className="email-row__action"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleEmailReadStatus(email?.id, true);
                    }}
                    disabled={loading}
                    title="Mark as read"
                    aria-label="Mark as read"
                  >
                    <i className="mdi mdi-email-open-outline"></i>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
